import React, { useState } from 'react';
import './Diet.css';

const formatResponse = (text) => {
  // Handle tables first
  text = text.replace(/\|([\s\S]*?)\n\n/g, (table) => {
    const rows = table.split('\n')
      .filter(row => row.trim() && !row.match(/^\s*[-|]+\s*$/));

    if (rows.length < 2) return table;

    return `<div class="diet-table-wrapper"><table class="diet-table">${
      rows.map((row, i) => {
        const cells = row
          .split('|')
          .filter(cell => cell.trim())
          .map(cell => cell.trim());
        
        return `<tr>${
          cells.map(cell => 
            `<${i === 0 ? 'th' : 'td'}>${cell}</${i === 0 ? 'th' : 'td'}>`
          ).join('')
        }</tr>`;
      }).join('')
    }</table></div>`;
  });

  // Handle other markdown formatting
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/### (.*?)\n/g, '<h3>$1</h3>')
    .replace(/## (.*?)\n/g, '<h2>$2</h2>')
    .replace(/# (.*?)\n/g, '<h1>$1</h1>')
    .replace(/^\d\. (.*)$/gm, '<div class="numbered-item">$1</div>')
    .replace(/^- (.*)$/gm, '<div class="bullet-item">• $1</div>')
    .split('\n').map(line => line.trim() ? `<div>${line}</div>` : '<br/>').join('');
};

const Diet = () => {
  const [state, setState] = useState({
    prompt: '',
    response: '',
    loading: false,
    generatedImage: '',
    imageLoading: false
  });

  const API_KEY = 'AIzaSyCNMwDLTs4C8MmumR3PDrbb6RPSgjR-1fs';
  const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

  const generateImage = async (dietPlan) => {
    setState(s => ({ ...s, imageLoading: true }));
    try {
      const response = await fetch(`${API_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: `Generate a beautiful, appetizing image that represents this diet plan: ${dietPlan}` }]
          }],
          generationConfig: { responseModalities: ["Text", "Image"] }
        })
      });

      const data = await response.json();
      if (data.candidates?.[0]?.content?.parts?.[0]?.data) {
        setState(s => ({ ...s, generatedImage: data.candidates[0].content.parts[0].data }));
      }
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setState(s => ({ ...s, imageLoading: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!state.prompt.trim()) return;
    
    setState(s => ({ ...s, loading: true, response: '', generatedImage: '' }));
    
    try {
      const result = await fetch(`${API_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: `Create a detailed diet plan based on: ${state.prompt}. 
                     Format your response using markdown:
                     - Use **bold** for important terms
                     - Use *italics* for emphasis
                     - Use ### for section headers
                     - Use bullet points (- ) for lists
                     - Use numbered lists (1. ) for steps
                     - Create tables using | for nutritional info and meal plans
                     
                     Include these sections with emojis:
                     ### 🎯 Overview
                     (Brief summary)
                     
                     ### 🍽️ Weekly Meal Plan
                     (Create a table with days and meals)
                     | Day | Breakfast | Lunch | Dinner | Snacks |
                     |-----|-----------|-------|---------|--------|
                     
                     ### 📊 Nutritional Breakdown
                     (Create a table with nutritional values)
                     | Nutrient | Amount | % Daily Value |
                     |----------|---------|--------------|
                     
                     ### 💡 Tips and Guidelines
                     (Bullet points with recommendations)`
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 1024,
          }
        })
      });

      const data = await result.json();
      if (data.candidates?.[0]?.content?.parts?.[0]?.text) {
        const formattedResponse = formatResponse(data.candidates[0].content.parts[0].text);
        setState(s => ({ ...s, response: formattedResponse }));
        await generateImage(data.candidates[0].content.parts[0].text);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error:', error);
      setState(s => ({
        ...s,
        response: '❌ Sorry, there was an error generating your diet plan. Please try again.'
      }));
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  };

  const { prompt, response, loading, generatedImage, imageLoading } = state;

  return (
    <div className="diet-container">
      <h1>🥗 AI Diet Maker</h1>
      <p>✨ Describe your dietary requirements, goals, preferences, or restrictions below:</p>
      
      <form onSubmit={handleSubmit} className="diet-form">
        <textarea
          value={prompt}
          onChange={(e) => setState(s => ({ ...s, prompt: e.target.value }))}
          placeholder="E.g., I'm a 30-year-old female looking to lose weight. I'm vegetarian and allergic to nuts. I exercise 3 times a week."
          className="diet-input"
          rows={4}
        />
        <button type="submit" className="diet-button" disabled={loading || !prompt.trim()}>
          {loading ? '🔄 Generating...' : '✨ Generate Diet Plan'}
        </button>
      </form>
      
      {(loading || imageLoading || response) && (
        <div className="diet-response">
          <h2>🎉 Your Personalized Diet Plan</h2>
          
          {(loading || imageLoading) && (
            <div className="loading-indicator">
              {loading ? '🤖 Creating your diet plan...' : '🖼️ Generating visual inspiration...'}
            </div>
          )}

          {generatedImage && (
            <div className="diet-image">
              <img 
                src={`data:image/jpeg;base64,${generatedImage}`} 
                alt="AI Generated Diet Visualization" 
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
          
          {response && (
            <div className="diet-content" 
                 dangerouslySetInnerHTML={{ __html: response }} 
            />
          )}
        </div>
      )}
      
      <div className="diet-tips">
        <h3>💡 Tips for better recommendations:</h3>
        <ul>
          <li>📋 Include your age, gender, weight, and height</li>
          <li>🏃‍♀️ Mention your activity level</li>
          <li>🚫 Specify any dietary restrictions or allergies</li>
          <li>🎯 Include your goals (weight loss, muscle gain, etc.)</li>
          <li>😋 Mention any foods you enjoy or dislike</li>
        </ul>
      </div>
    </div>
  );
};

export default Diet;
